import { onMounted, onUnmounted, unref, watch } from 'vue';

function readMeta(attr, key) {
  const el = document.querySelector(`meta[${attr}="${key}"]`);
  return el?.getAttribute('content') ?? null;
}

function writeMeta(attr, key, value) {
  if (!value) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function readCanonicalHref() {
  return document.querySelector('link[rel="canonical"]')?.getAttribute('href') ?? null;
}

export function writeCanonical(href) {
  if (!href) return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export { writeCanonical as ensureCanonicalLink };

/**
 * Met à jour title, canonical, og:url + meta description/OG pour une landing marketing SPA.
 * Accepte un objet, une ref ou un computed. Restaure les valeurs au démontage.
 */
export function useMarketingPageMeta(metaSource) {
  let previousTitle = null;
  let previousCanonical = null;
  const previousMeta = {};
  let snapshotted = false;

  function snapshotPrevious() {
    if (snapshotted) return;
    snapshotted = true;
    previousTitle = document.title;
    previousCanonical = readCanonicalHref();
    [
      ['name', 'description'],
      ['property', 'og:title'],
      ['property', 'og:description'],
      ['property', 'og:url'],
      ['name', 'twitter:title'],
      ['name', 'twitter:description'],
      ['name', 'robots'],
    ].forEach(([attr, key]) => {
      previousMeta[`${attr}:${key}`] = readMeta(attr, key);
    });
  }

  function apply() {
    const meta = unref(metaSource);
    if (!meta?.title) return;
    snapshotPrevious();
    document.title = meta.title;

    const canonicalUrl = meta.canonicalUrl || meta.ogUrl || null;
    if (canonicalUrl) {
      writeCanonical(canonicalUrl);
    }

    const entries = [
      ['name', 'description', meta.description],
      ['property', 'og:title', meta.ogTitle || meta.title],
      ['property', 'og:description', meta.ogDescription || meta.description],
      ['property', 'og:url', meta.ogUrl || meta.canonicalUrl],
      ['name', 'twitter:title', meta.ogTitle || meta.title],
      ['name', 'twitter:description', meta.ogDescription || meta.description],
      ['name', 'robots', meta.robots],
    ];
    entries.forEach(([attr, key, value]) => writeMeta(attr, key, value));
  }

  onMounted(() => {
    apply();
    watch(metaSource, apply, { deep: true });
  });

  onUnmounted(() => {
    if (previousTitle != null) document.title = previousTitle;
    if (previousCanonical != null) {
      writeCanonical(previousCanonical);
    }
    Object.entries(previousMeta).forEach(([compoundKey, value]) => {
      const [attr, key] = compoundKey.split(':');
      if (value == null) {
        document.querySelector(`meta[${attr}="${key}"]`)?.remove();
        return;
      }
      writeMeta(attr, key, value);
    });
  });
}
